import { createContext, useContext } from 'react';
import type { ComponentProps, ElementType, ReactNode } from 'react';

import { Box } from '@/shared/ui';
import Typography from '@/shared/ui/typography/Text';

import type { ExtraProps } from 'react-markdown';
import type { Sx } from '@/shared/types/uiTypes';

import { slugify } from './slugify';
import type { MarkdownHeading } from './useMarkdownHeadings';

const InsideBlockquoteContext = createContext(false);

// Plain, immutable data — RecapH3 looks its own id up by source line number
// rather than consuming from a shared position counter. A counter mutated
// during render doesn't survive React StrictMode's double-invocation of
// render bodies (dev only): the mutation from the first, discarded pass
// isn't undone before the second, causing every id after the first heading
// to shift by one. A pure lookup by line number has no such state to drift.
export const RecapHeadingsContext = createContext<MarkdownHeading[] | null>(null);

const getPlainText = (node: ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getPlainText).join('');
  return '';
};

const blockquoteSx: Sx = {
  backgroundColor: 'gx.tileDark',
  borderLeft: '4px solid',
  borderLeftColor: 'gx.highlight',
  px: 2,
  py: 2,
  my: 1.5,
  mx: 0
};

export const RecapBlockquote: ElementType<ComponentProps<'blockquote'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <Box
    {...props}
    component='blockquote'
    sx={blockquoteSx}
  >
    <InsideBlockquoteContext value>{children}</InsideBlockquoteContext>
  </Box>
);

const topLevelHeadingSx: Sx = {
  fontSize: '16px',
  fontWeight: 700,
  color: 'gx.text',
  pb: 1,
  mt: 3,
  mb: 1.5,
  borderBottom: '1px solid rgba(248, 248, 248, 0.2)'
};

const calloutHeadingSx: Sx = {
  fontSize: '14px',
  fontWeight: 700,
  color: 'gx.highlight',
  mb: 1
};

export const RecapH3: ElementType<ComponentProps<'h3'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => {
  const insideBlockquote = useContext(InsideBlockquoteContext);
  const headings = useContext(RecapHeadingsContext);

  const line = node?.position?.start.line;
  const matchedHeading = line !== undefined && headings?.find(heading => heading.line === line);
  const id = matchedHeading ? matchedHeading.id : slugify(getPlainText(children));

  return (
    <Typography
      {...props}
      component='h3'
      id={id}
      sx={insideBlockquote ? calloutHeadingSx : topLevelHeadingSx}
    >
      {children}
    </Typography>
  );
};
